import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, ShieldCheck, FileDown } from 'lucide-react';
import {
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_SCHEMA,
  BUSINESS_PHONE_TEL,
  COMPLIANCE_DISCLOSURE_TEXT,
  FOOTER_COPYRIGHT_TEXT,
  LEGAL_CONSENT_DOCUMENT_PATH,
  PORTAL_LINKS,
} from '../constants/site';

const Footer = () => {
  const companyLinks = [
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'FAQ', path: '/faq' },
  ];

  const personalLinks = [
    { name: 'Car Finance', path: '/car-loans' },
    { name: 'Personal Loans', path: '/personal-loans' },
    { name: 'Leisure Finance', path: '/leisure-finance' },
    { name: 'Apply', path: PORTAL_LINKS.GENERIC, external: true },
  ];

  const businessLinks = [
    { name: 'Asset Finance', path: '/vehicle-finance' },
    { name: 'Commercial Loans', path: '/business-loans' },
    { name: 'Equipment Finance', path: '/equipment-finance' },
    { name: 'Truck & Trailer', path: '/truck-and-trailer-finance' },
  ];

  const legalLinks = [
    { name: 'Disclosure', path: '/disclosure' },
    { name: 'Privacy', path: LEGAL_CONSENT_DOCUMENT_PATH, external: true, newTab: true },
    { name: 'Terms', path: '/terms' },
    { name: 'Complaints', path: '/complaints' },
    { name: 'How It Works', path: '/how-it-works' },
  ];

  return (
    <footer className="border-t border-purple-100 bg-gradient-to-b from-white to-[#f6f2fc]" data-testid="site-footer">
      <script
        type="application/ld+json"
        data-testid="footer-organization-schema"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Flash Funding',
            url: 'https://www.flashfunding.loan',
            telephone: BUSINESS_PHONE_SCHEMA,
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: BUSINESS_PHONE_SCHEMA,
              contactType: 'customer service',
              areaServed: 'AU',
            },
            address: { '@type': 'PostalAddress', addressCountry: 'AU' },
          }),
        }}
      />
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-purple-200 bg-white p-6 sm:p-8" data-testid="footer-top-card">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr]">
            <div className="space-y-5">
              <div className="h-10 w-[116px] overflow-hidden sm:h-11 sm:w-[130px] md:h-[60px] md:w-[142px]" data-testid="footer-brand-logo-wrap">
                <img
                  src="https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/i5qmq2zt_Copy%20of%20Flash%20Funding.png"
                  alt="Flash Funding"
                  className="h-full w-full object-cover object-[43%_50%]"
                  data-testid="footer-brand-logo"
                />
              </div>
              <p className="text-sm leading-relaxed text-[#4F456C]" data-testid="footer-brand-description">
                Broker-guided personal and business finance support with lender matching, structured applications, and clear next-step guidance.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="footer-proof-pills">
                <span className="rounded-full bg-purple-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-purple-700" data-testid="footer-proof-lenders">40+ lenders</span>
                <span className="rounded-full bg-purple-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-purple-700" data-testid="footer-proof-speed">Fast approvals</span>
                <span className="rounded-full bg-purple-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-purple-700" data-testid="footer-proof-legwork">No credit impact quote</span>
              </div>
              <div className="space-y-4 pt-2" data-testid="footer-contact-stack">
                <a href={BUSINESS_PHONE_TEL} className="flex items-center gap-3 rounded-lg px-1 py-0.5 text-sm leading-6 text-[#4F456C] transition-colors hover:text-purple-700" data-testid="footer-business-phone-link">
                  <Phone size={16} className="shrink-0 text-purple-600" />
                  <span className="font-medium tracking-[0.01em]">{BUSINESS_PHONE_DISPLAY}</span>
                </a>
                <a href="mailto:hello@flashfunding.loan" className="flex items-center gap-3 rounded-lg px-1 py-0.5 text-sm leading-6 text-[#4F456C] transition-colors hover:text-purple-700" data-testid="footer-email-link">
                  <Mail size={16} className="shrink-0 text-purple-600" />
                  <span className="font-medium tracking-[0.01em]">hello@flashfunding.loan</span>
                </a>
                <span className="flex items-center gap-3 rounded-lg px-1 py-0.5 text-sm leading-6 text-[#4F456C]" data-testid="footer-location-text">
                  <MapPin size={16} className="shrink-0 text-purple-600" /> Australia-wide service coverage
                </span>
              </div>
              <div className="flex gap-3" data-testid="footer-social-links">
                <a href="https://www.facebook.com/profile.php?id=61556267555240" target="_blank" rel="noopener noreferrer" className="text-[#665A86] transition-colors hover:text-purple-700" aria-label="Facebook" data-testid="footer-facebook-link"><Facebook size={18} /></a>
                <a href="https://www.instagram.com/flash_funding/" target="_blank" rel="noopener noreferrer" className="text-[#665A86] transition-colors hover:text-purple-700" aria-label="Instagram" data-testid="footer-instagram-link"><Instagram size={18} /></a>
              </div>
            </div>

            <FooterColumn title="Company" testId="footer-company-links" links={companyLinks} />
            <FooterColumn title="Personal Finance" testId="footer-personal-links" links={personalLinks} />
            <FooterColumn title="Business Finance" testId="footer-business-links" links={businessLinks} />
            <FooterColumn title="Legal" testId="footer-legal-links" links={legalLinks} />
          </div>

          <div className="mt-8 border-t border-purple-100 pt-6">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-purple-700" />
                <h4 className="text-xs font-heading font-bold uppercase tracking-[0.12em] text-[#1B1431]" data-testid="footer-credit-heading">Authorised Credit Representative</h4>
              </div>
              <a
                href={LEGAL_CONSENT_DOCUMENT_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center gap-2 rounded-full border border-purple-300 bg-purple-50 px-4 text-xs font-semibold uppercase tracking-[0.12em] text-purple-700 transition-[background-color] hover:bg-purple-100"
                data-testid="credit-guide-download"
              >
                <FileDown size={14} />
                Credit Guide & Privacy (PDF)
              </a>
            </div>
            <p className="text-xs leading-relaxed text-[#5D527D]" data-testid="footer-trust-strip">
              {COMPLIANCE_DISCLOSURE_TEXT}
            </p>
          </div>

          <div className="mt-6 border-t border-purple-100 pt-6">
            <h4 className="mb-3 text-xs font-heading font-bold uppercase tracking-[0.12em] text-[#1B1431]" data-testid="footer-disclaimer-heading">Important Information & Disclaimer</h4>
            <div className="space-y-3 text-xs leading-relaxed text-[#6E628D]" data-testid="footer-disclaimer-body">
              <p data-testid="footer-disclaimer-line-1">
                All applications are subject to the credit provider&apos;s credit assessment and loan eligibility criteria. Terms, conditions, fees and charges apply.
              </p>
              <p data-testid="footer-disclaimer-line-2">
                Information provided is factual information only, and is not intended to imply any recommendation about any financial product(s) or constitute tax advice. If you require financial or tax advice you should consult a licensed financial or tax adviser.
              </p>
              <p data-testid="footer-disclaimer-line-3">
                Your interest rate may be different and is based on a number of factors, including your credit history, the information you provide and our assessment of your application. We&apos;ll confirm your interest rate upon submission of your application.
              </p>
              <p data-testid="footer-disclaimer-line-4">
                <span className="font-semibold">WARNING:</span> Comparison rates apply only to the example(s) provided. Different amounts and terms will result in different comparison rates. Costs such as redraw or early repayment fees are not included but may influence total loan cost.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-purple-100 pt-6 md:flex-row">
            <p className="text-xs text-[#6E628D]" data-testid="footer-copyright-text">
              {FOOTER_COPYRIGHT_TEXT}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={LEGAL_CONSENT_DOCUMENT_PATH} target="_blank" rel="noopener noreferrer" className="text-xs text-[#6E628D] transition-colors hover:text-purple-700" data-testid="footer-privacy-link">Privacy Policy</a>
              <Link to="/terms" className="text-xs text-[#6E628D] transition-colors hover:text-purple-700" data-testid="footer-terms-link">Terms of Service</Link>
              <Link to="/disclosure" className="text-xs text-[#6E628D] transition-colors hover:text-purple-700" data-testid="footer-disclosure-link">Disclosure</Link>
              <Link to="/complaints" className="text-xs text-[#6E628D] transition-colors hover:text-purple-700" data-testid="footer-complaints-link">Complaints</Link>
              <Link to="/reviews" className="text-xs text-[#6E628D] transition-colors hover:text-purple-700" data-testid="footer-reviews-link">Reviews</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function FooterColumn({ title, links, testId }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-heading font-bold uppercase tracking-[0.14em] text-[#1B1431]" data-testid={`footer-column-heading-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{title}</h4>
      <ul className="space-y-3" data-testid={testId}>
        {links.map((link) => (
          <li key={`${link.path}-${link.name}`}>
            {link.external ? (
              <a
                href={link.path}
                target={link.newTab ? '_blank' : undefined}
                rel={link.newTab ? 'noopener noreferrer' : undefined}
                className="text-sm text-[#4F456C] transition-colors hover:text-purple-700"
                data-testid={`footer-link-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {link.name}
              </a>
            ) : (
              <Link
                to={link.path}
                className="text-sm text-[#4F456C] transition-colors hover:text-purple-700"
                data-testid={`footer-link-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {link.name}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Footer;
